import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus, faTrash, faSearch, faFilter, faChartLine, faDownload, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

// ✅ Removed Zoom plugin from imports and registration
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ArcElement);

// CSV Export Function
const exportToCSV = (analyticsData, filteredDownloaders) => {
    const headers = ['User Name', 'Download Date', 'User ID'];
    const csvContent = [
        headers.join(','),
        ...filteredDownloaders.map(d => [d.userName, d.dateDownloaded || ''].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `download_analytics_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const VisualBriefDownloaders = () => {
    const location = useLocation();
    const authState = useSelector(state => state.authReducer);
    const navigate = useNavigate();
    const [fetchData, { loading }] = useFetch();
    const [vbMasterId, setVbMasterId] = useState(location.state?.vbMasterId || '');
    const [vbTitle, setVbTitle] = useState(location.state?.title || '');

    useEffect(() => {
        setVbMasterId(location.state?.vbMasterId || '');
        setVbTitle(location.state?.title || '');
    }, [location.state]);

    const [downloaders, setDownloaders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    const fetchDownloadersList = useCallback(() => {
        const config = {
            url: "/vbdownloaded/log",
            method: "post",
            headers: { Authorization: authState.token },
            data: { vbMasterId: vbMasterId }
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                setDownloaders(response.vbdownloaded || []);
            })
            .catch(error => console.error("Error fetching Access Logs: ", error));
    }, [authState.token, fetchData, vbMasterId]);

    useEffect(() => {
        fetchDownloadersList();
    }, [fetchDownloadersList]);

    const handleDeleteClick = (vbDownloaderId) => {
        const config = { 
            url: `/vbdownloaded/${vbDownloaderId}`, 
            method: "delete", 
            headers: { Authorization: authState.token } 
        };
        fetchData(config)
            .then(() => fetchDownloadersList())
            .catch(error => console.error('Failed to delete the record:', error));
    };

    const formatDateTime = (date) => {
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };

    const filteredDownloaders = useMemo(() => {
        return downloaders.filter(downloader => {
            const matchesSearch = downloader.userName.toLowerCase().includes(searchTerm.toLowerCase());
            const downloaderDate = new Date(downloader.dateDownloaded);
            const matchesDateFrom = !filterDateFrom || downloaderDate >= new Date(filterDateFrom);
            const matchesDateTo = !filterDateTo || downloaderDate <= new Date(filterDateTo);
            return matchesSearch && matchesDateFrom && matchesDateTo;
        });
    }, [downloaders, searchTerm, filterDateFrom, filterDateTo]);

    const analyticsData = useMemo(() => {
        const dateCounts = {};
        const userDownloads = {};
        downloaders.forEach(downloader => {
            // const date = downloader.dateDownloaded.split(' ')[0];
            const date = downloader?.dateDownloaded?.split(' ')?.[0] || '';
            dateCounts[date] = (dateCounts[date] || 0) + 1;
            userDownloads[downloader.userName] = (userDownloads[downloader.userName] || 0) + 1;
        });

        const sortedDates = Object.keys(dateCounts).sort();
        const sortedUsers = Object.entries(userDownloads).sort(([,a], [,b]) => b - a);
        const averageDownloadsPerUser = downloaders.length > 0 ? downloaders.length / Object.keys(userDownloads).length : 0;
        const mostActiveDay = sortedDates.length > 0 ? sortedDates.reduce((maxDate, date) => dateCounts[date] > dateCounts[maxDate] ? date : maxDate, sortedDates[0]) : 'N/A';

        const userPieData = {
            labels: Object.keys(userDownloads).slice(0, 10),
            datasets: [{
                data: Object.values(userDownloads).slice(0, 10),
                backgroundColor: [
                    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
                    '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#84cc16'
                ],
                borderWidth: 1,
                borderColor: '#374151',
            }],
        };

        return {
            totalDownloads: downloaders.length,
            uniqueUsers: Object.keys(userDownloads).length,
            topUser: sortedUsers[0]?.[0] || 'N/A',
            topUserDownloads: sortedUsers[0]?.[1] || 0,
            averageDownloadsPerUser: Math.round(averageDownloadsPerUser * 100) / 100,
            mostActiveDay,
            mostActiveDayDownloads: mostActiveDay !== 'N/A' ? dateCounts[mostActiveDay] : 0,
            userPieData,
            chartData: {
                labels: sortedDates,
                datasets: [{
                    label: 'Downloads',
                    data: sortedDates.map(date => dateCounts[date]),
                    backgroundColor: 'rgba(20, 184, 166, 0.8)',
                    borderColor: 'rgba(20, 184, 166, 1)',
                    borderWidth: 1,
                }],
            }
        };
    }, [downloaders]);

    const renderDeleteIcon = (icon, onClick, tooltipText) => (
        <Tooltip text={tooltipText} position="top">
            <span
                className="p-2 rounded-full cursor-pointer text-red-400 hover:bg-red-500/20 hover:text-red-300"
                onClick={onClick}
            >
                <FontAwesomeIcon icon={icon} className="text-sm" />
            </span>
        </Tooltip>
    );

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
            legend: {
                position: 'top',
                labels: { color: '#e5e7eb' }
            },
            title: {
                display: true,
                text: 'Downloads Over Time',
                color: '#e5e7eb',
                font: { size: 16 }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#e5e7eb',
                bodyColor: '#e5e7eb',
                callbacks: {
                    label: (context) => `Downloads: ${context.parsed.y}`,
                    afterLabel: (context) => {
                        const date = analyticsData.chartData.labels[context.dataIndex];
                        const usersOnThatDay = downloaders.filter(d => d.dateDownloaded.startsWith(date)).map(d => d.userName);
                        return `Users: ${[...new Set(usersOnThatDay)].join(', ')}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { color: '#9ca3af' },
                grid: { color: '#374151' }
            },
            x: {
                ticks: { color: '#9ca3af', maxRotation: 45 },
                grid: { color: '#374151' }
            },
        },
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: { color: '#e5e7eb', padding: 20, usePointStyle: true, pointStyle: 'circle' }
            },
            title: {
                display: true,
                text: 'User Download Distribution',
                color: '#e5e7eb',
                font: { size: 16 }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#e5e7eb',
                bodyColor: '#e5e7eb',
                callbacks: {
                    label: (context) => `${context.label}: ${context.parsed} downloads`,
                    afterLabel: (context) => {
                        const percentage = ((context.parsed / analyticsData.totalDownloads) * 100).toFixed(1);
                        return `(${percentage}%)`;
                    }
                }
            },
        },
    };

    return (
        <div className='py-4 px-4 bg-black'>
            <header className="flex flex-col sm:flex-row items-start mb-2 border border-teal-500 bg-black">
                <h3 className="text-xl lg:text-3xl font-bold text-teal-300">VisualBrief Presentations</h3>
            </header>
            <div className="flex flex-col lg:flex-row justify-between items-start gap-2 mb-2">
                <h3 className='text-lg py-2 px-2 text-yellow-200'>PDF Download Log for {vbTitle}</h3>
                <Link
                    to="/visualbrief-myvb"
                    className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg shadow-lg"
                >
                    <FontAwesomeIcon icon={faPlus} /> Back to Library
                </Link>
            </div>

            {/* Search & Filters */}
            <section className="mb-6 bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="relative">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by user name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                    </div>
                    <div className="relative">
                        <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            value={filterDateFrom}
                            onChange={(e) => setFilterDateFrom(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                    </div>
                    <div className="relative">
                        <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                    </div>
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterDateFrom('');
                            setFilterDateTo('');
                        }}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg"
                    >
                        <FontAwesomeIcon icon={faFilter} /> Clear Filters
                    </button>
                </div>
                <p className="text-sm text-slate-400">
                    Showing {filteredDownloaders.length} of {downloaders.length} records
                </p>
            </section>

            {/* Charts */}
            <section className="mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                    <h4 className="text-xl font-bold text-teal-300 flex items-center gap-2">
                        <FontAwesomeIcon icon={faChartLine} /> Download Analytics & Engagement Metrics
                    </h4>
                    <button
                        onClick={() => exportToCSV(analyticsData, filteredDownloaders)}
                        className="flex items-center gap-2 w-40 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                    >
                        <FontAwesomeIcon icon={faDownload} /> Export CSV
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                        <p className="text-lg font-bold text-teal-400">{analyticsData.totalDownloads}</p>
                        <p className="text-slate-400">Total Downloads</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                        <p className="text-lg font-bold text-teal-400">{analyticsData.uniqueUsers}</p>
                        <p className="text-slate-400">Unique Users</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                        <p className="text-lg font-bold text-teal-400">{analyticsData.topUser} ({analyticsData.topUserDownloads})</p>
                        <p className="text-slate-400">Top Downloader</p>
                    </div>
                </div>

                {analyticsData.chartData.labels.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 h-96 relative">
                            <Bar options={chartOptions} data={analyticsData.chartData} />
                        </div>
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 h-96 relative">
                            <Pie options={pieOptions} data={analyticsData.userPieData} />
                        </div>
                    </div>
                )}
            </section>

            {/* List of downloaders */}
            {loading ? (
                <div className="h-[300px] flex items-center justify-center text-slate-400 text-lg font-medium">
                    Loading records...
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredDownloaders.length === 0 ? (
                        <div className="h-[300px] flex items-center justify-center text-slate-400 text-lg font-medium">
                            No records found!
                        </div>
                    ) : (
                        filteredDownloaders.map((downloader) => (
                            <article
                                key={downloader._id}
                                className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg hover:shadow-teal-500/25 transition-all duration-200"
                            >
                                <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="text-lg text-slate-200">{downloader.userName}</div>
                                        <div className="text-sm text-slate-400 flex items-center gap-2">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-teal-400" />
                                            {formatDateTime(downloader.dateDownloaded)}
                                        </div>
                                    </div>
                                    {renderDeleteIcon(faTrash, () => handleDeleteClick(downloader._id), 'Delete Log Entry')}
                                </div>
                            </article>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default VisualBriefDownloaders;
